import { cx } from '@chakra-ui/utils';
import { useContext } from 'react';
import type {
  OptionTypeBase,
  Props as ReactSelectProps,
  ValueContainerProps as ReactSelectValueContainerProps,
} from 'react-select';

import {
  Box,
  type BoxProps,
  TagGroup,
  Center,
  type TagGroupProps,
  TAG_GROUP_GAPS,
  useTheme,
} from '@frontend/components';
import {
  AssistanceAvailableIcon,
  AssistanceRejectedIcon,
} from '@frontend/components/src/components/mediaAndIcons/icons/iconSet';

import { SelectContext } from '../SelectContext';
import { useSelectStyles } from '../SelectStylesContext';
import { BASE_CLASS_NAME } from '../const';

export type ValueContainerProps<
  OptionType extends OptionTypeBase,
  IsMulti extends boolean,
> = ReactSelectValueContainerProps<OptionType, IsMulti> & {
  innerProps?: BoxProps;
  selectProps: ReactSelectProps<OptionType, IsMulti>;
};

export const ValueContainer = <
  OptionType extends OptionTypeBase,
  IsMulti extends boolean,
>({
  children,
  innerProps,
  selectProps,
  hasValue,
}: ValueContainerProps<OptionType, IsMulti>) => {
  const theme = useTheme();
  const {
    valueContainer: valueContainerStyles,
    suggestionsAvailableIcon: suggestionsAvailableIconStyles,
    suggestionsRejectedIcon: suggestionsRejectedIconStyles,
  } = useSelectStyles();

  const { hasSuggestions, hasAcceptedSuggestions, hasRejectedSuggestions } =
    useContext(SelectContext);

  const shouldShowSuggestionsAvailableIcon =
    (hasSuggestions && !hasRejectedSuggestions) || hasAcceptedSuggestions;
  const shouldShowSuggestionsRejectedIcon =
    hasSuggestions && hasRejectedSuggestions && !hasAcceptedSuggestions;

  const tagGroupSize: TagGroupProps['size'] =
    selectProps.size === 'sm' ? 'xs' : 'sm';

  const multiSelectProps: BoxProps & TagGroupProps = {
    as: TagGroup,
    size: tagGroupSize,
    paddingY: hasValue
      ? `calc(${theme.space[TAG_GROUP_GAPS[tagGroupSize]]} - 1px)`
      : undefined,
  };

  return (
    <Box
      {...innerProps}
      className={cx(
        `${BASE_CLASS_NAME}__value-container`,
        innerProps?.className,
      )}
      {...(selectProps.isMulti ? multiSelectProps : null)}
      __css={valueContainerStyles}
    >
      {children}
      {shouldShowSuggestionsAvailableIcon ||
      shouldShowSuggestionsRejectedIcon ? (
        <Center flex={1} justifyContent="flex-end">
          {shouldShowSuggestionsAvailableIcon ? (
            <AssistanceAvailableIcon __css={suggestionsAvailableIconStyles} />
          ) : null}
          {shouldShowSuggestionsRejectedIcon ? (
            <AssistanceRejectedIcon __css={suggestionsRejectedIconStyles} />
          ) : null}
        </Center>
      ) : null}
    </Box>
  );
};
